/**
 * `selfCheck` (W1-07b formats-package; contract formats/types.ts `SelfCheckFn`; DESIGN.md §5.11 step 4, §7.1;
 * ARCHITECTURE.md §11): the strict check every export runs on the bytes it produced. The lenient importer is never
 * allowed to silently repair our own output, so this FAILS on:
 *  - any decoder diagnostic with severity 'phiraRejects' or 'phiraPanics' (BOM, leading whitespace, missing
 *    "META", invalid UTF-8, float in an integer field, wrong types, ...);
 *  - an integer field written with a fraction or exponent (checked on the lossless JSON tree, independently of the
 *    decoder);
 *  - any non-empty `normalizeRpe` report (classes 'normalized' / 'changed'), any data normalisation would move to
 *    phiedit.json, and any repair the importer makes after normalising (repairIr.ts: notes out of time order,
 *    control points out of order, negative link groups, unusable BPMs, a negative RPEVersion);
 *  - any field-by-field difference between the DECODED chart (before normalisation) and the IR that was written
 *    (compare.ts: null vs absent, passthrough order, f32 through Math.fround, raw triples, RPEVersion lexemes).
 *
 * `bytes` may be the chart.json bytes (chart-only export) or a whole package. For a package it also checks what
 * makes Phira import it: a readable flat zip (Stored / Deflate, no root folder, no `export.json`, no name ending in
 * `click.png`, ASCII names), an info.yml that parses with Phira's typing rules, names the chart and `format: rpe`
 * and writes both speed / attach-UI switches explicitly; an extra.json exactly when the IR has one, decoding with no
 * Phira-fatal problem, no repair and the same effects and videos; a phiedit.json anchored to the chart bytes; and
 * every file Phira loads from the package (P§2.8) in the archive: the song, illustration and unlock video info.yml
 * names, line textures other than `line.png`, note hitsounds other than the built-in sounds, custom shaders
 * (`"/path"`) and videos of extra.json, each read from the written bytes. A missing one is a difference (Phira fails
 * the chart; for the illustration it shows a black background instead) unless the exporter knowingly left it out
 * (`SelfCheckOptions.knownMissing`: assets marked missing, which the checker reports and the user exported anyway).
 * The speed mode for normalising the chart comes from info.yml (`useRpe170Speed`), for a bare chart from the
 * options (default Phira's default, legacy). Whether a line texture is a GIF is decided as Phira decides it: by the
 * bytes in the archive (by the IR for a bare chart, or for a texture the archive lacks).
 */

import type { IrChart, IrExtraJson } from '../../core/ir/types';
import { customShaderPath, decodeExtra } from '../extra/decode';
import { resolveManifestDetailed, resolvePackagePath } from '../info/resolve';
import type { InfoYml, PackageFs } from '../info/types';
import { parseJsonLossless } from '../json/lexer';
import { decodeRpe } from '../rpe/decode';
import type { RpeChart, RpeDecoded } from '../rpe/json';
import { defaultTextureKind, normalizeRpeDetailed } from '../rpe/normalize';
import type { RpeSpeedMode } from '../rpe/normalize';
import type { SelfCheckFn, SelfCheckResult } from '../types';
import { checkIntegerTyping, compareChart, compareExtra, Diffs } from './compare';
import { sha256Hex } from './hash';
import { BUILTIN_HITSOUND_NAMES } from './names';
import { repairIrForModel, reportNoteOrder } from './repairIr';
import { PackageReport } from './report';
import { parseSidecar, SIDECAR_FILE } from './sidecar';
import { sniffMedia } from './sniff';
import { looksLikeZip, unzipDetailed } from './zip';

export interface SelfCheckOptions {
  /** Speed integrator for a bare chart.json (a package uses its info.yml). Default 'legacy'. */
  readonly speedMode?: RpeSpeedMode;
  /** Max differences listed (the count is always complete). Default 50. */
  readonly maxDiffs?: number;
  /**
   * Package paths the exporter knowingly did not write: assets marked missing (they have no bytes; the checker
   * reports them as PH-01 / PH-02 and the user chose to export anyway). A reference to one is not a difference.
   */
  readonly knownMissing?: ReadonlySet<string>;
}

/** A read-only directory view over unzipped entries. */
export function mapFs(entries: ReadonlyMap<string, Uint8Array>): PackageFs {
  const list = [...entries.keys()];
  return {
    list: () => list,
    has: (path) => entries.has(path),
    read: (path) => entries.get(path) ?? null,
  };
}

function isAscii(s: string): boolean {
  for (let i = 0; i < s.length; i++) if (s.charCodeAt(i) >= 0x80) return false;
  return true;
}

type TextureKindFn = (path: string) => 'gif' | 'image';

/** Whether a line texture is a GIF by the IR (the export's own view; a bare chart has nothing else). */
function irTextureKind(chart: IrChart): TextureKindFn {
  const kinds = new Map<string, 'gif' | 'image'>();
  for (const line of chart.judgeLineList) {
    if (line.textureKind !== 'builtin') kinds.set(line.Texture, line.textureKind);
  }
  return (path) => kinds.get(path) ?? defaultTextureKind(path);
}

/** Whether a line texture is a GIF by the bytes in the archive, as Phira decides it (the IR for a missing file). */
function archiveTextureKind(fs: PackageFs, chart: IrChart): TextureKindFn {
  const fallback = irTextureKind(chart);
  const cache = new Map<string, 'gif' | 'image'>();
  return (path) => {
    const cached = cache.get(path);
    if (cached !== undefined) return cached;
    const p = resolvePackagePath(path);
    const bytes = p === null ? null : fs.read(p);
    const kind = p === null || bytes === null ? fallback(path) : sniffMedia(bytes, p).codec === 'gif' ? 'gif' : 'image';
    cache.set(path, kind);
    return kind;
  };
}

/**
 * Files Phira loads from the package, collected per path (one difference per file, at its first reference) and
 * looked up in the archive the way Phira resolves them (`resolvePackagePath`).
 */
class FileRefs {
  private readonly refs = new Map<string, { readonly name: string; readonly where: string; uses: number }>();
  private readonly fs: PackageFs;
  private readonly knownMissing: ReadonlySet<string>;

  constructor(fs: PackageFs, knownMissing: ReadonlySet<string>) {
    this.fs = fs;
    this.knownMissing = knownMissing;
  }

  /** A file referenced as `name` at `where` (a JSON path or a manifest field). */
  use(name: string, where: string): void {
    const key = resolvePackagePath(name) ?? name;
    const ref = this.refs.get(key);
    if (ref === undefined) this.refs.set(key, { name, where, uses: 1 });
    else ref.uses++;
  }

  /** A difference for every referenced file the archive lacks, with what Phira does without it. */
  report(d: Diffs, consequence: string): void {
    for (const [key, ref] of this.refs) {
      if (this.fs.has(key) || this.knownMissing.has(key)) continue;
      const times = ref.uses > 1 ? ` (referenced ${String(ref.uses)} times)` : '';
      d.add(ref.where, `"${ref.name}" is not in the package${times}; ${consequence}`);
    }
  }
}

const FAILS_CHART = 'Phira fails to load the chart';

/**
 * The files info.yml names itself (as written, before Phira's fix_info_with looks for others) must be in the
 * package. An empty song or illustration name means the project has none (the checker's PH-02 for the song).
 */
function checkManifestFiles(fs: PackageFs, raw: InfoYml, knownMissing: ReadonlySet<string>, d: Diffs): void {
  const music = new FileRefs(fs, knownMissing);
  if (raw.music !== '') music.use(raw.music, 'info.yml music');
  music.report(d, 'Phira cannot play the chart');
  const illustration = new FileRefs(fs, knownMissing);
  if (raw.illustration !== '') illustration.use(raw.illustration, 'info.yml illustration');
  illustration.report(d, 'Phira shows a black background instead');
  const unlock = new FileRefs(fs, knownMissing);
  if (raw.unlockVideo !== null) unlock.use(raw.unlockVideo, 'info.yml unlockVideo');
  unlock.report(d, FAILS_CHART);
}

/** Line textures (except `line.png`) and custom note hitsounds of the chart as written (P§2.8): hard errors. */
function checkChartFiles(chart: RpeChart, fs: PackageFs, knownMissing: ReadonlySet<string>, d: Diffs): void {
  const refs = new FileRefs(fs, knownMissing);
  chart.judgeLineList.forEach((line, li) => {
    if (line.Texture !== 'line.png') refs.use(line.Texture, `judgeLineList[${String(li)}].Texture`);
    (line.notes ?? []).forEach((note, ni) => {
      const h = note.hitsound;
      if (h === undefined || h === null || BUILTIN_HITSOUND_NAMES.has(h)) return;
      refs.use(h, `judgeLineList[${String(li)}].notes[${String(ni)}].hitsound`);
    });
  });
  refs.report(d, FAILS_CHART);
}

/**
 * Custom shaders and videos of extra.json as written (P§5.1: a shader or video that fails to load fails the chart).
 * A shader name that is neither built in nor a path is already one of extra.json's Phira-fatal problems.
 */
function checkExtraFiles(extra: IrExtraJson, fs: PackageFs, knownMissing: ReadonlySet<string>, d: Diffs): void {
  const refs = new FileRefs(fs, knownMissing);
  extra.effects.forEach((effect, i) => {
    const path = customShaderPath(effect.shader);
    if (path !== null) refs.use(path, `extra.json effects[${String(i)}].shader`);
  });
  extra.videos.forEach((video, i) => {
    refs.use(video.path, `extra.json videos[${String(i)}].path`);
  });
  refs.report(d, FAILS_CHART);
}

interface PackageParts {
  readonly fs: PackageFs;
  readonly chart: Uint8Array;
  readonly extra: Uint8Array | null;
  readonly speedMode: RpeSpeedMode;
}

async function checkPackage(
  bytes: Uint8Array,
  knownMissing: ReadonlySet<string>,
  d: Diffs,
): Promise<PackageParts | null> {
  let z: ReturnType<typeof unzipDetailed>;
  try {
    z = unzipDetailed(bytes);
  } catch (e) {
    d.add('package', `not a readable zip (${e instanceof Error ? e.message : String(e)})`);
    return null;
  }
  for (const issue of z.issues) d.add(`package ${issue.name}`, `zip entry problem: ${issue.kind}`);
  if (z.rootFolder !== null) d.add('package', `files are inside the folder "${z.rootFolder}" instead of the root`);
  for (const name of z.entries.keys()) {
    if (name === 'export.json') d.add(`package ${name}`, 'Phira would treat the package as a batch export');
    if (name.endsWith('click.png')) d.add(`package ${name}`, 'Phira would treat the package as a resource pack');
    if (!isAscii(name)) d.add(`package ${name}`, 'file name is not ASCII');
  }
  const fs = mapFs(z.entries);
  const m = resolveManifestDetailed(fs);
  if (m.source !== 'yml') d.add('info.yml', 'missing');
  for (const diag of m.diagnostics) {
    if (diag.phiraFatal || diag.code === 'encodingGuessed')
      d.add('info.yml', `${diag.code} ${diag.detail ?? ''}`.trim());
  }
  if (m.info.format !== 'rpe') d.add('info.yml format', `expected "rpe", found ${String(m.info.format)}`);
  if (m.info.useRpe170Speed === null) d.add('info.yml useRpe170Speed', 'not written explicitly');
  if (m.info.useAttachUiFix === null) d.add('info.yml useAttachUiFix', 'not written explicitly');
  if (m.source === 'yml') checkManifestFiles(fs, m.raw, knownMissing, d);
  const chartPath = resolvePackagePath(m.info.chart);
  const chart = chartPath === null ? null : fs.read(chartPath);
  if (chart === null) {
    d.add('info.yml chart', `"${m.info.chart}" is not in the package`);
    return null;
  }
  const sidecar = fs.read(SIDECAR_FILE);
  if (sidecar !== null) {
    const parsed = parseSidecar(sidecar);
    if (!parsed.ok) d.add(SIDECAR_FILE, `unreadable (${parsed.reason})`);
    else if (parsed.sidecar.chartSha256 !== (await sha256Hex(chart)))
      d.add(SIDECAR_FILE, 'anchor does not match the chart');
  }
  return { fs, chart, extra: fs.read('extra.json'), speedMode: m.info.useRpe170Speed === true ? 'rpe170' : 'legacy' };
}

/** The chart bytes decoded as Phira reads them (null, with a difference, when they are not readable JSON). */
function decodeChart(chartBytes: Uint8Array, d: Diffs): RpeDecoded | null {
  try {
    return decodeRpe(chartBytes);
  } catch (e) {
    d.add('chart', `not readable JSON (${e instanceof Error ? e.message : String(e)})`);
    return null;
  }
}

function checkChart(
  chartBytes: Uint8Array,
  decoded: RpeDecoded,
  chart: IrChart,
  speedMode: RpeSpeedMode,
  textureKind: TextureKindFn,
  d: Diffs,
): SelfCheckResult['compared'] {
  for (const diag of decoded.diagnostics) {
    if (diag.severity === 'phiraRejects' || diag.severity === 'phiraPanics') {
      d.add(diag.path, `${diag.severity}: ${diag.code}${diag.detail === undefined ? '' : ` ${diag.detail}`}`);
    }
  }
  const text = new TextDecoder('utf-8', { fatal: false, ignoreBOM: true }).decode(chartBytes);
  const tree = parseJsonLossless(text);
  if (tree.ok) checkIntegerTyping(tree.node, typeof chart.META.RPEVersion === 'number', d);
  const norm = normalizeRpeDetailed(decoded, { textureKind, speedMode });
  for (const entry of norm.report) {
    d.add(entry.path ?? 'chart', `the importer would repair this (${entry.cls}: ${entry.message.key})`);
  }
  for (const entry of norm.sidecar) {
    d.add(`judgeLineList[${String(entry.line)}].${entry.key}`, 'the importer would move this to phiedit.json');
  }
  // The importer's last repairs before Lift (repairIr.ts) are repairs too.
  const model = new PackageReport();
  reportNoteOrder(norm.chart, model);
  repairIrForModel(norm.chart, model);
  for (const entry of model.entries()) {
    d.add(entry.path ?? 'chart', `the importer would repair this (${entry.cls}: ${entry.message.key})`);
  }
  return compareChart(decoded, chart, d);
}

/** Checks extra.json against the IR; returns what Phira reads from it (null when the package has none). */
function checkExtra(
  bytes: Uint8Array | null,
  expected: IrExtraJson | null,
  chart: IrChart,
  d: Diffs,
): IrExtraJson | null {
  if (expected === null) {
    if (bytes !== null) d.add('extra.json', 'written although the project has no effects or videos');
  } else if (bytes === null) d.add('extra.json', 'missing');
  if (bytes === null) return null;
  const ex = decodeExtra(bytes, chart.BPMList);
  for (const key of ex.phiraFatal) d.add('extra.json', `Phira would refuse it (${key})`);
  if (expected === null) return ex.extra;
  // encodeExtra writes the chart's BPMs as f64 (CR-W1-07b-7), so a converted BPM list is a real mismatch again.
  for (const entry of ex.report) {
    if (entry.cls === 'changed' || entry.cls === 'normalized') {
      d.add(`extra.json ${entry.path ?? ''}`.trim(), `the importer would repair this (${entry.message.key})`);
    }
  }
  compareExtra(expected, ex.extra, d);
  return ex.extra;
}

/** The strict self-check with options (see the module header). */
export async function selfCheckDetailed(
  bytes: Uint8Array,
  expected: { readonly chart: IrChart; readonly extra: IrExtraJson | null },
  opts: SelfCheckOptions = {},
): Promise<SelfCheckResult> {
  const d = new Diffs(opts.maxDiffs ?? 50);
  const knownMissing = opts.knownMissing ?? new Set<string>();
  let compared: SelfCheckResult['compared'] = { lines: 0, events: 0, notes: 0 };
  if (looksLikeZip(bytes)) {
    const parts = await checkPackage(bytes, knownMissing, d);
    if (parts !== null) {
      const decoded = decodeChart(parts.chart, d);
      if (decoded !== null) {
        const kind = archiveTextureKind(parts.fs, expected.chart);
        compared = checkChart(parts.chart, decoded, expected.chart, parts.speedMode, kind, d);
        checkChartFiles(decoded.chart, parts.fs, knownMissing, d);
      }
      const extra = checkExtra(parts.extra, expected.extra, expected.chart, d);
      if (extra !== null) checkExtraFiles(extra, parts.fs, knownMissing, d);
    }
  } else {
    const decoded = decodeChart(bytes, d);
    if (decoded !== null) {
      const kind = irTextureKind(expected.chart);
      compared = checkChart(bytes, decoded, expected.chart, opts.speedMode ?? 'legacy', kind, d);
    }
  }
  const diffs = [...d.list];
  if (d.total > d.list.length) diffs.push(`(+${String(d.total - d.list.length)})`);
  return { ok: d.empty, diffs, compared };
}

export const selfCheck: SelfCheckFn = (bytes, expected) => selfCheckDetailed(bytes, expected);
